import { useState } from 'react';
import { Link } from 'react-router-dom';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://everythingautomation.ai';

const pricingPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'AI Automation Pricing Plans | Everything Automation',
  description: 'Done-for-you AI automation systems. One-time setup fee plus monthly subscription. Starter from £149/mo, Pro £299/mo, Scale £599/mo. AI-powered booking, lead capture and CRM automation for UK businesses.',
  url: `${siteUrl}/pricing`,
  publisher: {
    '@type': 'Organization',
    name: 'Everything Automation',
    url: siteUrl,
  },
  mainEntity: {
    '@type': 'ItemList',
    name: 'Everything Automation Pricing Plans',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Offer',
          name: 'Starter Plan',
          description: 'Automated lead capture system. AI chatbot, single channel integration, basic lead capture, FAQ auto-responses, appointment booking, and Google Sheets and Calendar integration.',
          price: '149',
          priceCurrency: 'GBP',
          priceSpecification: { '@type': 'RecurringCharge', billingPeriod: 'P1M' },
          seller: { '@type': 'Organization', name: 'Everything Automation' },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Offer',
          name: 'Pro Plan',
          description: 'Automated sales and booking system. Multi-channel automation, advanced chatbot, full booking system with confirmations and reminders, CRM automation, follow-up sequences, email automation, and review automation.',
          price: '299',
          priceCurrency: 'GBP',
          priceSpecification: { '@type': 'RecurringCharge', billingPeriod: 'P1M' },
          seller: { '@type': 'Organization', name: 'Everything Automation' },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Offer',
          name: 'Scale Plan',
          description: 'Fully automated business system. Custom automation workflows, unlimited integrations, custom chatbot logic, advanced CRM, staff training, monthly optimisation, priority support, and a dedicated account manager.',
          price: '599',
          priceCurrency: 'GBP',
          priceSpecification: { '@type': 'RecurringCharge', billingPeriod: 'P1M' },
          seller: { '@type': 'Organization', name: 'Everything Automation' },
        },
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Offer',
          name: 'Custom / Enterprise Plan',
          description: 'Fully custom automation system for businesses that have outgrown standard systems. Custom-built workflows, deep integrations, multi-location setup, advanced AI logic, and dedicated specialist support.',
          price: '799',
          priceCurrency: 'GBP',
          priceSpecification: { '@type': 'RecurringCharge', billingPeriod: 'P1M' },
          seller: { '@type': 'Organization', name: 'Everything Automation' },
        },
      },
    ],
  },
};

type FeatureItem = {
  text: string;
  groupLabel?: string;
};

type Plan = {
  name: string;
  systemTitle: string;
  outcome: string;
  price: string;
  period: string;
  setupFee: string;
  tagline: string;
  highlight: boolean;
  badge: string | null;
  features: FeatureItem[];
  cta: string;
  roi: { stat: string; label: string; note: string };
};

const plans: Plan[] = [
  {
    name: 'Starter',
    systemTitle: 'Automated Lead Capture System',
    outcome: 'Capture leads you would normally miss',
    price: '£149',
    period: '/mo',
    setupFee: 'One-time setup: £199 – £299',
    tagline: 'Capture and organise every lead automatically with a simple AI-powered system.',
    highlight: false,
    badge: null,
    features: [
      { text: 'AI chatbot on your website that answers enquiries 24/7' },
      { text: '1-channel integration (WhatsApp, Instagram, SMS, or Facebook)' },
      { text: 'Automatically capture every lead, even while you sleep' },
      { text: 'Instantly answer customer questions without lifting a finger' },
      { text: 'Automatically book appointments without manual back-and-forth' },
      { text: 'Capture email addresses from every enquiry automatically' },
      { text: 'Automated follow-up that keeps leads warm until they are ready to book' },
      { text: 'Google Sheets CRM so every lead is organised in one place' },
      { text: 'Google Calendar sync so bookings go straight into your diary' },
    ],
    cta: 'Get Started',
    roi: {
      stat: '£400+',
      label: 'additional monthly revenue',
      note: 'Typically captures £400+ in additional monthly revenue from missed enquiries.',
    },
  },
  {
    name: 'Pro',
    systemTitle: 'Automated Sales & Booking System',
    outcome: 'Turn more enquiries into booked appointments',
    price: '£299',
    period: '/mo',
    setupFee: 'One-time setup: £399 – £699',
    tagline: 'A fully automated sales and booking system working across all channels.',
    highlight: true,
    badge: 'Most Popular',
    features: [
      { text: 'Everything in Starter, plus:' },
      { text: 'Automate lead capture across WhatsApp, Instagram, Facebook, SMS, and Email', groupLabel: 'Multi-channel Automation' },
      { text: 'Advanced chatbot that qualifies leads and moves them towards booking' },
      { text: 'Full booking system with automated confirmations, reminders, and rescheduling', groupLabel: 'Booking & Scheduling' },
      { text: 'Automated follow-up system that converts more leads into paying clients', groupLabel: 'Follow-up & Nurture' },
      { text: 'Nurture leads automatically until they are ready to book' },
      { text: 'Recover missed leads before they go to a competitor' },
      { text: 'Automate review requests to build your reputation on autopilot', groupLabel: 'Growth & Re-engagement' },
      { text: 'Re-engage cold leads and past clients automatically' },
      { text: 'Full lead tracking so you always know where every prospect stands', groupLabel: 'CRM & Pipeline' },
      { text: 'Clear pipeline visibility so nothing falls through the cracks' },
    ],
    cta: 'Book a Demo (Best Option)',
    roi: {
      stat: '£1,800+',
      label: 'additional monthly revenue',
      note: 'Typically generates £1,800+ in additional monthly revenue through improved lead capture and automated follow-ups.',
    },
  },
  {
    name: 'Scale',
    systemTitle: 'Fully Automated Business Operating System',
    outcome: 'Run your entire lead and booking system on autopilot',
    price: '£599',
    period: '/mo',
    setupFee: 'One-time setup: £799 – £1,499',
    tagline: 'A complete AI-powered system running your leads, bookings, and follow-ups.',
    highlight: false,
    badge: null,
    features: [
      { text: 'Everything in Pro, plus:' },
      { text: 'Professional website design & build included at a discounted rate, conversion-ready and automation-ready from day one', groupLabel: 'Website Discount' },
      { text: 'Fully custom automation workflows built around your exact business processes', groupLabel: 'Custom Automation' },
      { text: 'Advanced chatbot logic tailored to your brand and customer journey' },
      { text: 'Multi-step workflows that replace entire manual processes end-to-end' },
      { text: 'Unlimited integrations connecting every tool in your business', groupLabel: 'Integrations' },
      { text: 'Instant staff notifications so your team always knows what is happening', groupLabel: 'Internal Operations' },
      { text: 'Automate repetitive internal tasks so your team focuses on what matters' },
      { text: 'Automated client onboarding from first contact to first appointment' },
      { text: 'Custom internal workflows that eliminate manual admin entirely' },
      { text: 'Custom dashboards giving you full visibility across leads and revenue', groupLabel: 'Reporting & Insights' },
      { text: 'Live performance tracking so you can see exactly what is working' },
      { text: 'Automated invoicing so you get paid faster with less chasing', groupLabel: 'Payments' },
      { text: 'Payment reminders that recover overdue invoices automatically' },
      { text: 'Full staff training and onboarding so your team gets results from day one', groupLabel: 'Service & Support' },
      { text: 'Monthly system optimisation to keep your automation improving over time' },
      { text: 'Priority support with fast response times' },
      { text: 'Dedicated account manager who knows your business inside out' },
    ],
    cta: 'Speak to an Expert',
    roi: {
      stat: '£4,000+',
      label: 'monthly value delivered',
      note: 'Delivers £4,000+ in monthly value across lead handling, bookings, and customer management.',
    },
  },
  {
    name: 'Custom',
    systemTitle: 'Fully Custom Automation System',
    outcome: 'Fully customised automation built for your business',
    price: 'Custom Pricing',
    period: '',
    setupFee: 'One-time setup: From £1,000+',
    tagline: 'Fully tailored automation designed around your business, systems, and growth goals.',
    highlight: false,
    badge: null,
    features: [
      { text: 'Fully custom-built automation systems tailored to your business' },
      { text: 'Handle high volumes of leads, bookings, and customer interactions' },
      { text: 'Advanced AI chatbot logic built around your exact workflows' },
      { text: 'Deep integrations with CRMs, tools, and custom systems' },
      { text: 'Multi-location or multi-team automation setup' },
      { text: 'Automate internal operations, staff workflows, and onboarding' },
      { text: 'Custom dashboards and performance tracking' },
      { text: 'Ongoing optimisation and system scaling' },
      { text: 'Priority support', groupLabel: 'Service Layer' },
      { text: 'Direct automation specialist access' },
      { text: 'Ongoing strategy and scaling support' },
    ],
    cta: 'Request Custom Plan',
    roi: {
      stat: 'Bespoke',
      label: 'pricing scoped to your business',
      note: 'Fully custom quote based on your automation scope, team size, and growth targets.',
    },
  },
];

const industryRoi = [
  {
    id: 'hair',
    icon: 'ri-scissors-2-line',
    label: 'Hair & Beauty',
    image: 'https://public.readdy.ai/ai/img_res/8ddc78787dc6722ecbf41968cc53d786.jpg',
    recommended: 'Pro',
    roiPoints: [
      { icon: 'ri-calendar-close-line', text: 'Salons average 4-6 no-shows per week. At £40 per slot, that is £640-960 lost monthly. Automation cuts no-shows by up to 40%.' },
      { icon: 'ri-moon-line', text: '70% of salon enquiries come outside business hours. Without AI, these are lost. With it, they become bookings.' },
      { icon: 'ri-repeat-line', text: 'Rebooking automation fills 6-month check-up slots automatically, generating consistent recurring revenue without any outbound effort.' },
    ],
    paybackLine: 'A single no-show prevented per week pays for the Starter plan.',
  },
  {
    id: 'dental',
    icon: 'ri-heart-pulse-line',
    label: 'Dental & Clinics',
    image: 'https://readdy.ai/api/search-image?query=modern%20dental%20clinic%20reception%20area%20clean%20white%20interior%20professional%20medical%20office%20calm%20clinical%20atmosphere%20premium%20healthcare%20facility&width=400&height=260&seq=pricing-roi-dental&orientation=landscape',
    recommended: 'Pro',
    roiPoints: [
      { icon: 'ri-time-line', text: 'Receptionists spend an average of 2 hours per day on inbound calls and booking management. AI removes that entirely.' },
      { icon: 'ri-user-add-line', text: 'Clinics that respond to new patient enquiries within 5 minutes convert at 2x the rate of those who respond within 30 minutes.' },
      { icon: 'ri-calendar-2-line', text: 'Recall automation fills 6-month check-up slots automatically, generating consistent recurring revenue without any outbound effort.' },
    ],
    paybackLine: 'One extra new patient per month covers the cost of the Pro plan.',
  },
  {
    id: 'trades',
    icon: 'ri-hammer-line',
    label: 'Builders & Trades',
    image: 'https://readdy.ai/api/search-image?query=professional%20construction%20builder%20working%20modern%20home%20renovation%20cinematic%20lighting%20tools%20equipment%20premium%20quality%20tradespeople%20worksite&width=400&height=260&seq=pricing-roi-trades&orientation=landscape',
    recommended: 'Scale',
    roiPoints: [
      { icon: 'ri-search-eye-line', text: '70% of homeowners hire the first tradesperson to respond. AI guarantees you are always that person, even at 11pm on a Sunday.' },
      { icon: 'ri-mail-check-line', text: 'Quote follow-up sequences recover 30-40% of jobs that had gone cold, generating significant additional revenue with zero effort.' },
      { icon: 'ri-award-line', text: 'Automated review requests build your Google and Checkatrade profile, increasing organic lead volume month on month.' },
    ],
    paybackLine: 'One recovered job from follow-up automation pays for a full year of the Pro plan.',
  },
  {
    id: 'fitness',
    icon: 'ri-run-line',
    label: 'Fitness & Wellness',
    image: 'https://readdy.ai/api/search-image?query=modern%20bright%20gym%20fitness%20studio%20clean%20equipment%20minimal%20design%20natural%20light%20wooden%20floors%20professional%20wellness%20centre&width=400&height=260&seq=pricing-roi-fitness&orientation=landscape',
    recommended: 'Pro',
    roiPoints: [
      { icon: 'ri-group-line', text: '55% of gym leads who do not hear back within 1 hour go elsewhere. Instant AI response captures the sale before they move on.' },
      { icon: 'ri-bar-chart-line', text: 'Automated check-ins at weeks 2, 6, and month 3 reduce drop-off by up to 35%, meaning more consistent monthly revenue.' },
      { icon: 'ri-refresh-line', text: 'Re-engagement sequences for lapsed members recover 15-20% of churned clients who would otherwise stay cancelled.' },
    ],
    paybackLine: 'Retaining two members per month pays for the Pro plan many times over.',
  },
  {
    id: 'home-services',
    icon: 'ri-home-heart-line',
    label: 'Home Services',
    image: 'https://readdy.ai/api/search-image?query=professional%20home%20cleaning%20service%20modern%20bright%20clean%20house%20interior%20minimalist%20living%20room%20premium%20quality%20domestic%20cleaning%20light%20tones&width=400&height=260&seq=pricing-roi-home&orientation=landscape',
    recommended: 'Starter',
    roiPoints: [
      { icon: 'ri-calendar-check-line', text: 'Recurring job reminders and day-before confirmations eliminate last-minute cancellations, keeping your schedule full every week.' },
      { icon: 'ri-share-line', text: 'Automated referral requests generate 3x more word-of-mouth leads without any manual outreach from you.' },
      { icon: 'ri-star-line', text: 'Post-job review requests build your profile on Google and Bark, increasing inbound organic enquiries month on month.' },
    ],
    paybackLine: 'One extra job per week from referral automation pays for the Pro plan.',
  },
  {
    id: 'physio',
    icon: 'ri-stethoscope-line',
    label: 'Physio & Therapy',
    image: 'https://readdy.ai/api/search-image?query=professional%20physiotherapy%20clinic%20treatment%20room%20calm%20serene%20minimal%20modern%20equipment%20clean%20neutral%20tones%20premium%20healthcare%20practice%20interior&width=400&height=260&seq=pricing-roi-physio&orientation=landscape',
    recommended: 'Pro',
    roiPoints: [
      { icon: 'ri-user-add-line', text: 'Responding to new patient enquiries instantly converts at 45% higher rates than practices that reply manually the next day.' },
      { icon: 'ri-heart-add-line', text: '68% of therapy clients rebook if they receive a timely follow-up. Without automation, most of these rebooks simply never happen.' },
      { icon: 'ri-file-text-line', text: 'Pre-session intake automation saves 20 minutes of admin per new patient, adding up to hours reclaimed every week.' },
    ],
    paybackLine: 'Two extra initial assessments per month more than cover the Pro plan.',
  },
];

const comparisonRows = [
  { label: 'AI Chatbot', vals: ['Basic', 'Advanced', 'Fully Custom', 'Enterprise'] },
  { label: 'Channel Integration', vals: ['1 Channel', 'Multi-channel', 'Unlimited', 'Unlimited'] },
  { label: 'Lead Capture System', vals: [true, true, true, true] },
  { label: 'FAQ Auto-responses', vals: [true, true, true, true] },
  { label: 'Appointment Booking', vals: ['Basic', 'Full System', 'Full System', 'Full System'] },
  { label: 'Google Sheets CRM', vals: [true, true, true, true] },
  { label: 'Google Calendar', vals: [true, true, true, true] },
  { label: 'Multi-channel Automation', vals: [false, true, true, true] },
  { label: 'Booking Confirmations and Reminders', vals: [false, true, true, true] },
  { label: 'CRM Automation and Lead Tracking', vals: [false, true, true, true] },
  { label: 'Follow-up Sequences', vals: [false, true, true, true] },
  { label: 'Email Automation', vals: [false, true, true, true] },
  { label: 'Review and Re-engagement Automation', vals: [false, true, true, true] },
  { label: 'Custom Automation Workflows', vals: [false, false, true, true] },
  { label: 'Unlimited n8n Integrations', vals: [false, false, true, true] },
  { label: 'Advanced CRM Integrations', vals: [false, false, true, true] },
  { label: 'Staff Training and Onboarding', vals: [false, false, true, true] },
  { label: 'Monthly Optimisation', vals: [false, false, true, true] },
  { label: 'Dedicated Account Manager', vals: [false, false, true, true] },
  { label: 'Priority Support', vals: [false, false, true, true] },
  { label: 'Multi-location / Multi-team Setup', vals: [false, false, false, true] },
  { label: 'Direct Automation Specialist Access', vals: [false, false, false, true] },
  { label: 'Ongoing Strategy & Scaling Support', vals: [false, false, false, true] },
];

const faqs = [
  {
    q: 'How does the pricing model work?',
    a: 'Every plan has two parts: a one-time setup fee and a monthly subscription. The setup fee covers building and configuring your automation system from scratch. The monthly subscription covers ongoing maintenance, optimisation, monitoring, and support so your system keeps running and improving over time.',
  },
  {
    q: 'What is included in the setup fee?',
    a: 'The setup fee covers the full build of your automation system. This includes chatbot configuration, channel connections, booking flow setup, CRM integration, testing, and going live. You pay this once at the start. Everything after that is covered by the monthly plan.',
  },
  {
    q: 'How long does setup take?',
    a: 'Setup typically takes 30 to 60 days depending on the type and complexity of your automation. Starter systems go live closer to 30 days. Pro builds typically take 30 to 45 days. Scale and Custom systems can take up to 60 days given the depth of configuration, integrations, and testing involved. We handle everything. You do not need to do anything technical.',
  },
  {
    q: 'Do I need any technical knowledge?',
    a: 'Absolutely not. We handle all the technical setup. You just describe your business process and we do the rest. If you can send a message on your phone, you have all the skills you need.',
  },
  {
    q: 'Can I upgrade my plan later?',
    a: 'Yes, any time. Most clients start on Starter or Pro and upgrade once they see results coming in. Upgrades are seamless with no downtime and no disruption to your live systems.',
  },
  {
    q: 'Is there a contract or lock-in?',
    a: 'All monthly plans are month-to-month with no long-term contracts. Cancel any time with 30 days notice. We are confident in the results, so we do not need to lock you in.',
  },
  {
    q: 'What channels are included?',
    a: 'Starter covers website chat and one channel such as WhatsApp. Pro and Scale include every channel: Instagram, Facebook, WhatsApp, SMS, email, LinkedIn, TikTok, and website chat. If your customers use it, we can automate it.',
  },
  {
    q: 'Do you build websites too?',
    a: 'Yes. Alongside automation, we design and build professional websites tailored to your business. Every project is quoted individually based on your requirements, with pricing typically ranging from £200 to £800 depending on scope. We discuss everything on a call first, so the price always matches what you actually need.',
  },
  {
    q: 'What happens after I book a demo?',
    a: 'We jump on a 30-minute call, walk through your current setup, show you exactly what automation would look like for your business, and recommend the right plan. No pressure, no jargon. Just a practical walkthrough of what is possible.',
  },
  {
    q: 'How does the no-show prevention work?',
    a: 'Once a booking is confirmed, the system automatically sends a reminder 48 hours before and again 2 hours before the appointment. Clients can confirm, reschedule, or cancel directly from the message. This alone cuts no-shows by 35-40% on average.',
  },
];

const pricingFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.a,
    },
  })),
};

const PricingPage = () => {
  const [activeIndustry, setActiveIndustry] = useState('hair');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const activeRoi = industryRoi.find((i) => i.id === activeIndustry)!;

  return (
    <>
      <title>AI Automation Pricing | Done-for-You Systems from £149/mo | Everything Automation</title>
      <meta name="description" content="Done-for-you AI automation systems with a one-time setup fee and monthly subscription. Starter from £149/mo, Pro £299/mo, Scale £599/mo. AI-powered booking, lead capture and CRM automation for UK businesses. Book a free demo." />
      <meta name="keywords" content="AI automation pricing, done-for-you automation UK, booking automation cost, AI chatbot monthly plan" />
      <link rel="canonical" href={`${siteUrl}/pricing`} />
      <meta property="og:title" content="AI Automation Pricing | Done-for-You Systems | Everything Automation" />
      <meta property="og:description" content="Done-for-you AI automation systems. One-time setup fee plus monthly subscription. Starter £149/mo, Pro £299/mo, Scale £599/mo." />
      <meta property="og:url" content={`${siteUrl}/pricing`} />
      <meta property="og:type" content="website" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pricingPageSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(pricingFaqSchema) }} />
      <main className="bg-ea-black">
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{ backgroundImage: 'radial-gradient(circle, #0ea5e9 1px, transparent 1px)', backgroundSize: '48px 48px' }}
          />
          <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
              <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Done-for-You Automation Systems</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">
              Plans That Pay for{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Themselves
              </span>
            </h1>
            <p className="text-ea-gray text-lg lg:text-xl max-w-2xl mx-auto">
              We build your automation system once, then maintain, optimise, and support it monthly so your business runs 24/7 without you lifting a finger.
            </p>
          </div>
        </section>

        {/* Outcome Strip */}
        <section className="pb-10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {[
                {
                  icon: 'ri-tools-line',
                  label: 'We Build It for You',
                  desc: 'This is not software you configure yourself. We design, build, and deploy your automation system from scratch.',
                },
                {
                  icon: 'ri-24-hours-line',
                  label: '24/7 Automated System',
                  desc: 'Your system captures leads, books appointments, and follows up automatically around the clock.',
                },
                {
                  icon: 'ri-refresh-line',
                  label: 'Ongoing Optimisation',
                  desc: 'Monthly reviews, performance tracking, and continuous improvements included in every plan.',
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-3 sm:p-5 flex items-start gap-3 sm:gap-4"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 flex-shrink-0">
                    <i className={`${item.icon} text-ea-blue text-sm sm:text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-white font-semibold text-xs sm:text-sm mb-0.5 sm:mb-1">{item.label}</p>
                    <p className="text-ea-gray text-xs leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How Pricing Works */}
        <section className="pb-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="bg-gradient-to-r from-ea-blue/10 to-ea-blue/5 border border-ea-blue/25 rounded-2xl px-8 py-10 text-center md:text-left">
              <h2 className="text-white font-bold text-2xl mb-3">How Pricing Works</h2>
              <p className="text-ea-gray text-base leading-relaxed max-w-3xl md:mx-0 mx-auto mb-8">
                We build your automation system with a one-time setup fee.
                Then we maintain, optimise, and support it monthly so your business runs 24/7 without you needing to manage it.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 md:justify-start justify-center">
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl px-6 py-4 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 mx-auto mb-2">
                    <i className="ri-tools-line text-ea-blue text-lg"></i>
                  </div>
                  <p className="text-ea-blue font-bold text-base">One-time Setup Fee</p>
                  <p className="text-white text-xs font-medium mt-0.5">We build everything from scratch</p>
                  <p className="text-ea-gray text-xs mt-1">Paid once at the start</p>
                </div>
                <div className="hidden sm:flex items-center">
                  <i className="ri-add-line text-ea-gray text-xl"></i>
                </div>
                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl px-6 py-4 text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 mx-auto mb-2">
                    <i className="ri-refresh-line text-ea-blue text-lg"></i>
                  </div>
                  <p className="text-ea-blue font-bold text-base">Monthly Subscription</p>
                  <p className="text-white text-xs font-medium mt-0.5">We maintain, optimise, and grow it</p>
                  <p className="text-ea-gray text-xs mt-1">Ongoing support and improvements</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Plans */}
        <section className="pb-24 pt-4">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">

            {/* Above-grid headline */}
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Turn Your Business Into a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  24/7 Automated Sales Machine
                </span>
              </h2>
              <p className="text-ea-gray text-lg max-w-2xl mx-auto mb-6">
                Capture, convert and book clients automatically using AI-powered systems and integrations.
              </p>
              <div className="inline-flex items-start gap-3 bg-ea-blue/10 border border-ea-blue/25 rounded-xl px-6 py-4 max-w-2xl mx-auto">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-green-400">
                  <i className="text-xs ri-check-line"></i>
                </div>
                <p className="text-white/80 text-sm leading-relaxed text-left">
                  Instead of hiring staff to manage messages, bookings and follow-ups, our system does it for you 24/7.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6 items-stretch">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`relative rounded-2xl flex flex-col transition-all duration-300 ${
                    plan.highlight
                      ? 'bg-ea-blue/5 border-2 border-ea-blue'
                      : 'bg-ea-dark-card border border-ea-dark-border hover:border-ea-blue/30'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 bg-ea-blue text-white text-xs font-bold rounded-full whitespace-nowrap">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className="p-4 lg:p-7 flex flex-col flex-1">
                    <div className="mb-4 lg:mb-6">
                      <p className="text-ea-blue text-xs font-bold uppercase tracking-widest mb-1">{plan.name}</p>
                      <p className="text-white font-bold text-sm lg:text-lg leading-tight mb-1">{plan.systemTitle}</p>
                      <p className="text-ea-blue/70 text-xs font-medium mb-2 lg:mb-3 leading-snug">{plan.outcome}</p>
                      <div className="flex items-end gap-1.5 mb-1.5 flex-wrap">
                        <span className={`text-white font-bold leading-tight ${plan.period ? 'text-2xl lg:text-4xl' : 'text-xl lg:text-2xl'}`}>{plan.price}</span>
                        {plan.period && <span className="text-ea-gray text-xs lg:text-sm pb-1">{plan.period}</span>}
                      </div>
                      <div className="inline-flex items-center gap-1 bg-ea-blue/10 border border-ea-blue/20 rounded-lg px-2 py-1 mb-2 lg:mb-3">
                        <i className="ri-settings-3-line text-ea-blue text-xs"></i>
                        <span className="text-ea-blue text-xs font-bold">{plan.setupFee}</span>
                      </div>
                      <p className="text-ea-gray text-xs leading-relaxed hidden lg:block">{plan.tagline}</p>
                    </div>

                    {/* ROI highlight */}
                    <div className="bg-ea-blue/10 border border-ea-blue/20 rounded-xl p-3 lg:p-4 mb-4 lg:mb-6">
                      <p className="text-ea-blue font-bold text-lg lg:text-xl">{plan.roi.stat}</p>
                      <p className="text-white text-xs font-medium">{plan.roi.label}</p>
                      <p className="text-ea-gray text-xs mt-1 leading-relaxed hidden lg:block">{plan.roi.note}</p>
                    </div>

                    {/* Features */}
                    <ul className="space-y-1.5 flex-1 mb-4 lg:mb-8">
                      {plan.features.map((f, idx) => (
                        <li key={idx}>
                          {f.groupLabel && (
                            <p className="text-ea-blue/80 text-xs font-semibold uppercase tracking-wider mt-3 mb-1.5 first:mt-0">
                              {f.groupLabel}
                            </p>
                          )}
                          <div className="flex items-start gap-2">
                            <div className="w-4 h-4 flex items-center justify-center rounded-full mt-0.5 flex-shrink-0 text-green-400">
                              <i className="text-xs ri-check-line"></i>
                            </div>
                            <span className="text-xs lg:text-sm leading-snug text-white/90">{f.text}</span>
                          </div>
                        </li>
                      ))}
                    </ul>

                    {/* CTA */}
                    <Link
                      to="/contact"
                      className={`w-full text-center py-2.5 lg:py-3 rounded-lg font-semibold text-xs lg:text-sm transition-all duration-300 whitespace-nowrap cursor-pointer block ${
                        plan.highlight
                          ? 'bg-ea-blue hover:bg-ea-blue-light text-white'
                          : 'border border-ea-blue/50 hover:border-ea-blue text-ea-blue hover:bg-ea-blue/10'
                      }`}
                    >
                      {plan.cta}
                    </Link>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-center text-ea-gray text-xs mt-6">
              All plans include onboarding, initial setup, and a 30-day results review. Setup fees are quoted on your demo call and confirmed before any work begins.
            </p>
          </div>
        </section>

        {/* We Also Build Websites */}
        <section className="py-24 bg-ea-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Additional Service</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                We Also Build{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Websites
                </span>
              </h2>
              <p className="text-ea-gray text-lg max-w-2xl mx-auto">
                Need a professional website to go alongside your automation setup? We design and build clean, fast, conversion-focused sites tailored to your business.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div className="rounded-xl overflow-hidden w-full h-64 lg:h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20professional%20website%20design%20displayed%20on%20laptop%20and%20mobile%20screen%20dark%20background%20with%20glowing%20blue%20accent%20lights%20minimal%20clean%20UI%20web%20design%20showcase%20premium%20tech%20aesthetic&width=800&height=450&seq=website-service-img&orientation=landscape"
                  alt="Website design and build service"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div>
                <ul className="space-y-4 mb-8">
                  {[
                    { icon: 'ri-smartphone-line', text: 'Fully mobile-responsive design built for every screen size' },
                    { icon: 'ri-speed-line', text: 'Fast-loading, SEO-friendly structure from day one' },
                    { icon: 'ri-brush-line', text: 'Custom design tailored to your brand, not a template' },
                    { icon: 'ri-robot-2-line', text: 'Automation-ready, so your new site plugs straight into your systems' },
                    { icon: 'ri-customer-service-2-line', text: 'Ongoing support available after launch' },
                  ].map((item) => (
                    <li key={item.text} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-ea-blue text-sm`}></i>
                      </div>
                      <span className="text-ea-gray text-sm leading-relaxed">{item.text}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-6 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-price-tag-3-line text-ea-blue text-lg"></i>
                    </div>
                    <p className="text-white font-semibold text-base">Pricing tailored to you</p>
                  </div>
                  <p className="text-ea-gray text-sm leading-relaxed">
                    Every website is different, so every quote is too. Pricing is based on your specific requirements, discussed on a call. As a guide, our website projects typically range from{' '}
                    <span className="text-white font-medium">£200 to £800</span>, depending on scope and complexity. No surprises, no hidden fees.
                  </p>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-sm"
                  style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.25)' }}
                >
                  Discuss Your Website <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Industry ROI Section */}
        <section className="py-24 bg-ea-navy">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">ROI by Industry</span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                What Does This Actually{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Mean for Your Business?
                </span>
              </h2>
              <p className="text-ea-gray text-lg max-w-2xl mx-auto">
                Select your industry and see exactly how the numbers work out for a business like yours.
              </p>
            </div>

            {/* Industry Tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {industryRoi.map((ind) => (
                <button
                  key={ind.id}
                  onClick={() => setActiveIndustry(ind.id)}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeIndustry === ind.id
                      ? 'bg-ea-blue border-ea-blue text-white'
                      : 'bg-ea-dark-card border-ea-dark-border text-ea-gray hover:border-ea-blue/40 hover:text-white'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${ind.icon} text-sm`}></i>
                  </div>
                  {ind.label}
                </button>
              ))}
            </div>

            {/* Active Industry ROI Panel */}
            <div className="bg-ea-dark-card border border-ea-dark-border rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-64 lg:h-auto min-h-72 order-2 lg:order-1">
                  <img
                    src={activeRoi.image}
                    alt={activeRoi.label}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ea-dark-card hidden lg:block"></div>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-ea-dark-card lg:hidden"></div>
                </div>

                <div className="p-8 lg:p-10 order-1 lg:order-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-ea-blue/10 border border-ea-blue/20">
                      <i className={`${activeRoi.icon} text-ea-blue text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-white font-bold">{activeRoi.label}</p>
                      <p className="text-ea-blue text-xs">Recommended: {activeRoi.recommended} Plan</p>
                    </div>
                  </div>

                  <p className="text-ea-gray text-sm mb-6">Real numbers, specific to your sector:</p>

                  <div className="space-y-4 mb-8">
                    {activeRoi.roiPoints.map((point, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20 flex-shrink-0">
                          <i className={`${point.icon} text-ea-blue text-sm`}></i>
                        </div>
                        <p className="text-white/80 text-sm leading-relaxed">{point.text}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-lightbulb-line text-green-400 text-sm"></i>
                    </div>
                    <p className="text-green-300 text-sm font-medium leading-relaxed">{activeRoi.paybackLine}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Comparison */}
        <section className="py-24 bg-ea-black">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Full Breakdown</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-3">Feature Comparison</h2>
              <p className="text-ea-gray text-sm">See exactly what is included in each plan at a glance.</p>
            </div>

            <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl overflow-hidden">
              {/* Header */}
              <div className="grid grid-cols-5 border-b border-ea-dark-border">
                <div className="p-5 text-ea-gray text-sm font-medium">Feature</div>
                {plans.map((plan) => (
                  <div
                    key={plan.name}
                    className={`p-5 text-center text-sm font-bold ${plan.highlight ? 'text-ea-blue' : 'text-white'}`}
                  >
                    {plan.name}
                    {plan.highlight && (
                      <span className="block text-ea-blue/60 text-xs font-normal mt-0.5">Most Popular</span>
                    )}
                  </div>
                ))}
              </div>

              {comparisonRows.map((row, idx) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-5 border-b border-ea-dark-border/50 ${idx % 2 === 0 ? 'bg-ea-dark/30' : ''}`}
                >
                  <div className="p-4 text-white text-sm">{row.label}</div>
                  {row.vals.map((val, i) => (
                    <div key={i} className="p-4 text-center">
                      {typeof val === 'boolean' ? (
                        <div className="w-5 h-5 flex items-center justify-center mx-auto">
                          <i className={`text-base ${val ? 'ri-check-line text-ea-blue' : 'ri-close-line text-ea-gray/30'}`}></i>
                        </div>
                      ) : (
                        <span className="text-ea-blue text-xs font-medium">{val}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-24 bg-ea-navy">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-3">
                Frequently Asked{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Questions
                </span>
              </h2>
              <p className="text-ea-gray text-sm">Everything you need to know before booking a call.</p>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  className="bg-ea-dark-card border border-ea-dark-border rounded-xl overflow-hidden transition-all duration-200"
                >
                  <button
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer"
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  >
                    <span className="text-white font-medium text-sm">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 ml-4">
                      <i className={`text-ea-blue text-sm transition-transform duration-200 ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-ea-gray text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-ea-black">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-5">
              Not sure which plan is right?{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Let us decide together.
              </span>
            </h2>
            <p className="text-ea-gray text-lg mb-8">
              Book a free 30-minute demo call. We will show you exactly what automation would look like for your business, walk you through the setup process, and recommend the right plan.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-ea-blue hover:bg-ea-blue-light text-white font-bold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-base"
              style={{ boxShadow: '0 0 25px rgba(14, 165, 233, 0.3)' }}
            >
              Book a Free Demo <i className="ri-arrow-right-line"></i>
            </Link>
            <p className="text-ea-gray text-xs mt-4">No commitment required. No pushy sales calls. Just a practical walkthrough of what is possible.</p>
          </div>
        </section>
      </main>
    </>
  );
};

export default PricingPage;
